// Project: GalaxyComplete
// Created: 9/24/20 by sammy
// File: CurrentUserInfo

import * as React from "react";
import { observer } from "mobx-react-lite";
import { useAppServices } from "../app/services";
import {
    Avatar,
    Box,
    Chip,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Popover,
    SvgIcon,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { PopoverState, usePopoverState } from "../../common/popover/PopoverService";
import { LicenseKeyIcon, UserSettingsIcon } from "../project/ProjectCommon";
import { LogOutIcon } from "../../common/CommonIcons";
import { getAuthenticatedUserFullName, useNavigateToUserLicensePage, useNavigateToUserSettingsPage } from "../user/UserCommon";
import { useIsDesktop } from "./MainLayout";
import { AuthenticatedUserInfo } from "../../_proto/galaxycompletepb/apipb/auth_api_pb";
import { IoIosArrowDown } from "react-icons/io";
import { MdOutlineStar } from "react-icons/md";
import { HideInPrivateEdition } from "../auth/PrivateEditionView";
import { SiMicrosoftazure } from "react-icons/si";
import { useLogOut } from "../auth/auth_hooks";

// ======================
// CurrentUserInfo
// ======================

const useCurrentUserInfoStyles = () => {
    const t = useTheme();
    return {
        avatar: {
            backgroundColor: t.palette.secondary.main,
        },
        root: {
            minWidth: t.spacing(30),
        },
        mobile: {
            minWidth: t.spacing(10),
        },
    };
};

interface CurrentUserInfoProps {
    isDesktop: boolean;
}

export const CurrentUserInfo: React.FC<CurrentUserInfoProps> = observer((p) => {
    const styles = useCurrentUserInfoStyles();
    const userMenuState = usePopoverState();
    const { authService } = useAppServices();
    const theme = useTheme();

    const currentUser = authService.currentUser;
    if (!currentUser) {
        return null;
    }

    const avatarString = `${currentUser.getFirstName()[0]}${currentUser.getLastName()[0]}`;

    const name = (
        <Box display={"flex"} alignItems={"center"}>
            {getAuthenticatedUserFullName(currentUser)}
            &nbsp;
            {currentUser.getActiveAwsMpSub() && <MdOutlineStar size={"1.15em"} color={"#ffc107"} />}
            {currentUser.getActiveAzureMpSub() && <SiMicrosoftazure size={"1em"} color={theme.palette.primary.main} />}
        </Box>
    );

    return (
        <Box sx={p.isDesktop ? styles.root : styles.mobile}>
            <List>
                <ListItem dense button onClick={userMenuState.open} aria-label={"currentUserMenuButton"}>
                    <ListItemIcon>
                        <Avatar color={"secondary"} sx={styles.avatar} src={`${currentUser.getAvatar()}`}>
                            {avatarString}
                        </Avatar>
                    </ListItemIcon>
                    {p.isDesktop && <ListItemText primary={name} secondary={currentUser.getCompanyName()} />}
                    <SvgIcon>
                        <IoIosArrowDown />
                    </SvgIcon>
                </ListItem>
            </List>
            <UserPopoverMenu popoverState={userMenuState} currentUser={currentUser} />
        </Box>
    );
});

// ======================
// UserPopoverMenu
// ======================

interface UserPopoverMenuProps {
    popoverState: PopoverState;
    currentUser: AuthenticatedUserInfo;
}

export const UserPopoverMenu: React.FC<UserPopoverMenuProps> = observer((props) => {
    const { popoverState, currentUser } = props;
    const { authService } = useAppServices();
    const styles = useCurrentUserInfoStyles();
    const navigateToUserSettingsPage = useNavigateToUserSettingsPage();
    const navigateToUserLicensePage = useNavigateToUserLicensePage();
    const isDesktop = useIsDesktop();
    const logOut = useLogOut();

    const goToUserSettings = () => {
        navigateToUserSettingsPage();
        popoverState.close();
    };

    const goToUserLicense = () => {
        navigateToUserLicensePage();
        popoverState.close();
    };

    return (
        <Popover
            anchorEl={popoverState.anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            open={popoverState.isOpen}
            onClose={popoverState.close}
        >
            <Box sx={styles.root}>
                <List>
                    {!isDesktop && (
                        <>
                            <ListItem>
                                <ListItemText primary={getAuthenticatedUserFullName(currentUser)} secondary={currentUser.getCompanyName()} />
                            </ListItem>
                            <Divider />
                        </>
                    )}
                    {currentUser.getActiveAwsMpSub() && (
                        <>
                            <ListItem>
                                <ListItemText primary={"AWS Marketplace Subscription"} secondary={"Active Subscription"} />
                            </ListItem>
                            <Divider />
                        </>
                    )}

                    <ListItem button onClick={goToUserSettings}>
                        <ListItemIcon>
                            <UserSettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary={"My Profile"} />
                    </ListItem>
                    <Divider />
                    <HideInPrivateEdition>
                        <ListItem button onClick={goToUserLicense}>
                            <ListItemIcon>
                                <LicenseKeyIcon />
                            </ListItemIcon>
                            <ListItemText primary={"My License Key"} />
                        </ListItem>
                        <Divider />
                    </HideInPrivateEdition>
                    <ListItem button onClick={() => logOut()}>
                        <ListItemIcon>
                            <LogOutIcon />
                        </ListItemIcon>
                        <ListItemText primary={"Log Out"} />
                    </ListItem>
                </List>
            </Box>
        </Popover>
    );
});
